import { Button } from '@givebutter/primitives/button';
import { useMediaQuery } from '@givebutter/react-util';
import { IPlayerProps, Player } from '@lottiefiles/react-lottie-player';
import * as starburstJson from 'assets/js/animations/starburst.json';
import * as Dialog from 'components/dialog';
import { useAccountLocalStorage } from 'contexts/useAccountLocalStorage';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import MrButterSuperstar from 'images/mrbutter-superstar.svg';

interface RecommendedActionCompleteDialogProps extends Dialog.DialogProps {}
const RecommendedActionCompleteDialog = (props: RecommendedActionCompleteDialogProps) => {
    const { firstName } = useCurrentUser();
    const { setHasSeenCompleteAllRecommendedActionsDialog } = useAccountLocalStorage(
        ({ setHasSeenCompleteAllRecommendedActionsDialog }) => ({
            setHasSeenCompleteAllRecommendedActionsDialog,
        }),
    );
    const isMobile = useMediaQuery(`(max-width: 640px)`);

    return (
        <Dialog.Root
            {...props}
            onOpenChange={open => {
                if (!open) {
                    setHasSeenCompleteAllRecommendedActionsDialog(true);
                }
            }}
        >
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content className="max-w-[400px] items-center overflow-visible p-0">
                    {!isMobile && (
                        <div
                            className="absolute -left-[150%] -top-[150%] -z-10 flex h-[200%] w-[200%] items-center justify-center"
                            aria-hidden
                        >
                            <StarsAnimation speed={0.9} />
                        </div>
                    )}

                    <Dialog.Body className="gap-6 rounded-xl bg-white p-6">
                        <div className="flex flex-col gap-1">
                            <Dialog.Title>You're a superstar, {firstName} 🌟</Dialog.Title>
                            <p className="text-sm text-gray-600">
                                Congratulations on setting up the tools you need to make the most of
                                Givebutter! Now you're ready to skyrocket your fundraising.
                            </p>
                        </div>
                        <Dialog.Cancel asChild>
                            <Button variant="solid" className={'w-full'}>
                                Onwards!
                            </Button>
                        </Dialog.Cancel>
                    </Dialog.Body>

                    <div
                        className="motion-safe:animate-float sm:-top-30 absolute -top-28 ml-10"
                        aria-hidden
                    >
                        {isMobile && (
                            <div className="absolute -left-6 h-[110%] w-[110%]">
                                <StarsAnimation />
                            </div>
                        )}
                        <img src={MrButterSuperstar} alt="" className="relative" />
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

interface StarsAnimationProps extends Omit<IPlayerProps, 'src'> {}
const StarsAnimation = (props: StarsAnimationProps) => {
    return (
        <Player
            autoplay
            rendererSettings={{ preserveAspectRatio: 'xMidYMin slice' }}
            src={JSON.stringify(starburstJson)}
            className={`pointer-events-none absolute h-full w-full !overflow-visible [&>svg]:overflow-visible`}
            {...props}
        />
    );
};

export { RecommendedActionCompleteDialog };
